import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NGXLogger as LoggerService } from "ngx-logger";
import { TranslationGeneralService } from '../translation/translation.general.service';
import { DOCUMENT } from '@angular/common';
import { AlternateHrefService } from '../../locale/alternate-href.service';
import { environment } from '../../../environments/environment';
import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { filter, map, mergeMap, Subscription } from 'rxjs';
import { routesSeo as routesSeoEn } from '../../locale/en/routes-seo';
import { routesSeo as routesSeoDe } from '../../locale/de/routes-seo';
import { routesSeo as routesSeoEs } from '../../locale/es/routes-seo';
import { routesSeo as routesSeoFr } from '../../locale/fr/routes-seo';
import { routesSeo as routesSeoIt } from '../../locale/it/routes-seo';
import { routesSeo as routesSeoPt } from '../../locale/pt/routes-seo';
import { GoogleAnalyticsService } from './google-analytics.service';
import { HeaderClass, SAMPLE_POLL_LANGUAGES, SamplePollLanguage, buildPollMeta, buildSamplePollListMeta } from 'whocan-lib';
import { SamplePollSlugService } from '../../whoo/core/sample-poll-slug.service';
import { normalizeUrlForIndexCheck } from '../helpers/url-normalization';
import type { SamplePollResolvedKeys } from '../../whoo/features/sample-poll/sample-poll.resolver';

/** Lokalisierte Listing-Pfade pro Sprache (z.B. /poll-templates, /umfrage-vorlagen). */
const SAMPLE_POLLS_LIST_PATH_BY_LANG: Record<SamplePollLanguage, string> = {
    en: '/' + routesSeoEn.samplePollsList,
    de: '/' + routesSeoDe.samplePollsList,
    es: '/' + routesSeoEs.samplePollsList,
    fr: '/' + routesSeoFr.samplePollsList,
    it: '/' + routesSeoIt.samplePollsList,
    pt: '/' + routesSeoPt.samplePollsList,
};

export interface PollRoute {
    ownerId: string;
    pollId: string;
    voteUserId?: string;
}


@Injectable({
    providedIn: 'root',
})
export class RouteContextService {
    private _id: string;
    private renderer: Renderer2;
    private elementsToBeRemoved = [];
    private noIndexElement;
    private samplePollCanonicalElement;
    private samplePollHreflangElements = [];
    /**
     * Slug der aktuell aktiven Sample-Poll-Route (gesetzt vom Resolver via
     * setSamplePollAlternatesFromUrl, geloescht bei Navigation auf andere
     * Routen). Guard gegen Late-Writes: setupSamplePollAlternates laeuft nach
     * einem RTDB-Read und darf nach einem Routenwechsel keine Tags mehr auf
     * die falsche Seite schreiben.
     */
    private activeSamplePollSlug: string | null = null;
    private alternatUrls: string[] = [];
    private _pollRoute: PollRoute | null = null;
    private subscribtionRouteData: Subscription;
    private subscribtionRouterNavigationEnd: Subscription;
    constructor(
        private htmlTitle: Title,
        private htmlMeta: Meta,
        private logger: LoggerService,
        private trans: TranslationGeneralService,
        rendererFactory: RendererFactory2, // we cannot inject the renderer directly in a service
        @Inject(DOCUMENT) private document: any,
        private alternateHrefService: AlternateHrefService,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private googleAnalyticsService: GoogleAnalyticsService,
        private samplePollSlugService: SamplePollSlugService,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);
    }
    get id(): string {
        return this._id;
    }

    get title() {
        return this.trans.getShortTitle(this.id);
    }

    public subscribe() {
        this.subscribtionRouteData = this.subscribeRouteData()
    }

    public unsubscribe() {
        this.subscribtionRouteData.unsubscribe()
        this.subscribtionRouterNavigationEnd.unsubscribe()
    }

    get isVotePage(): boolean {
        //  this.logger.log('isVotePage', this.id)
        if (this.id === 'vote') return true
        return false;
    }

    private subscribeRouteData(): Subscription {
        return this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                map(() => this.activatedRoute),
                map((_route) => {
                    let route = <ActivatedRoute>_route;
                    while (route.firstChild) {
                        route = route.firstChild;
                    }
                    return route;
                }),
                filter((route) => (<ActivatedRoute>route).outlet === 'primary'),
                mergeMap((route) => (<ActivatedRoute>route).data.pipe(
                    map((data) => ({ data, params: (<ActivatedRoute>route).snapshot.params }))
                ))
            )
            .subscribe(({ data, params }) => {
                this.logger.log('subscribeRouteContext', data)
                this._pollRoute = this.getPollRoute(params);
                this.handleRouteDateChange(data);
            });
    }

    /**
     * The poll of the active route - set when the route carries `ownerId` and `pollId` as params
     * (poll responses, matrix, edit, vote, vote response, previews, test routes), null on My Events,
     * create, user pages, sample polls etc. Unlike `CurrentPollService`, whose context lingers after
     * leaving a poll route, this follows the URL (workorder poll-scoped access 1.7).
     */
    get pollRoute(): PollRoute | null {
        return this._pollRoute;
    }

    private getPollRoute(params: Params): PollRoute | null {
        if (!params?.['ownerId'] || !params?.['pollId']) return null;
        const pollRoute: PollRoute = { ownerId: params['ownerId'], pollId: params['pollId'] };
        if (params['voteUserId']) pollRoute.voteUserId = params['voteUserId'];
        return pollRoute;
    }

    private handleRouteDateChange(event) {
        this.alternatUrls = []
        this._id = event.id;
        this.logger.log('event', event);
        this.clearNoIndexElement();
        if (event.noIndex) {
            this.logger.log('event.noIndex')
            this.addNoIndex();
        }
        this.googleAnalyticsService.emitPageView(this.id, this.title)
        // Sample-Poll-Pages setzen Canonical + hreflang bereits im samplePollResolver rein
        // URL-basiert (ohne RTDB-Read) — VOR dem fuer Googlebot geblockten Firebase-Read, also
        // sichtbar selbst wenn die Route nie aktiviert und dieses NavigationEnd nie feuert.
        // setupSamplePollAlternates() (Page) verfeinert sie danach best-effort. Den
        // Sample-Poll-Set hier nicht anfassen — der Resolver-Set ist die garantiert sichtbare
        // Quelle. Der GENERISCHE Set (z.B. vom Listing bei SPA-Navigation Listing -> Detail)
        // muss aber weg, sonst stehen zwei Canonicals nebeneinander.
        if (event.id === 'samplePoll' || event.id === 'samplePollMatrix') {
            this.removeAlternateHrefLangs();
            return;
        }
        this.activeSamplePollSlug = null;
        if (event.id === 'samplePollsList') {
            // Listing-Metas kommen aus whocan-lib — identisch zum
            // samplePollList-Zweig des ogProxy (kein Drift Client/Function).
            const meta = buildSamplePollListMeta(this.samplePollLanguage(), environment.brandName);
            this.htmlTitle.setTitle(meta.title);
            this.htmlMeta.updateTag({ name: 'description', content: meta.description });
        } else {
            this.updateHtmlTitle(event.id);
            this.updateHtmlMetaDescription(event.id);
        }
        this.removeAlternateHrefLangs();
        this.removeSamplePollAlternates();
        this.addAlternateHrefLangs(event.id);
    }

    /**
     * Entfernt server-gerenderte Canonical-/hreflang-Links (ogProxy patcht sie
     * seit Schritt 2 in die Shell), bevor der Client eigene setzt — zwei
     * unterschiedliche Canonicals waeren schlimmer als gar keiner. Entfernen
     * statt Ueberspringen: der Client hat nach RTDB-Read ggf. bessere Daten
     * (resolvedLang), und ein Skip wuerde den Remove-Lebenszyklus bei
     * SPA-Navigation verkomplizieren. Eigene getrackte Elemente werden
     * uebersprungen (Idempotenz bei mehrfacher Navigation).
     */
    private removeServerRenderedSeoLinks() {
        const ownElements = new Set<Element>([
            ...this.elementsToBeRemoved,
            ...this.samplePollHreflangElements,
        ]);
        if (this.samplePollCanonicalElement) {
            ownElements.add(this.samplePollCanonicalElement);
        }
        const links = this.document.head.querySelectorAll(
            'link[rel="canonical"], link[rel="alternate"][hreflang]'
        );
        links.forEach((link: Element) => {
            if (!ownElements.has(link)) {
                this.renderer.removeChild(this.document.head, link);
            }
        });
    }

    private addAlternateHrefLangs(id: string) {
        this.logger.log('updateAlternateHrefLang', id);
        this.removeServerRenderedSeoLinks();
        const alternates = this.alternateHrefService.getAlternateHrefLangs(id);
        alternates.forEach((alternate) => {
            if (alternate.langCode === this.trans.locale) {
                this.insertCanonical(alternate.langCode, alternate.url)
            }
            this.logger.log('updateAlternateHrefLang alternate', alternate);
            this.insertHrefLang(alternate.langCode, alternate.url)
        });
        // x-default -> EN, identische Semantik wie im Sample-Poll-Detail-Pfad
        // (writeSamplePollHreflang). Bewusst im generischen Pfad: der Gap gilt
        // fuer jede indexierbare App-Route (Audit Befund 3).
        const enAlternate = alternates.find((alternate) => alternate.langCode === 'en');
        if (enAlternate) {
            this.insertXDefault(this.getUrlHost('en', enAlternate.url));
        }
        this.handleNoIndexCase();
    }

    private insertCanonical(langCode: string, url) {
        const linkElement = this.renderer.createElement('link');
        this.renderer.setAttribute(linkElement, 'rel', 'canonical');
        const canonicalUrl = this.getUrlHost(langCode, url);
        this.renderer.setAttribute(linkElement, 'href', canonicalUrl);
        this.insertElement(linkElement);
        this.elementsToBeRemoved.push(linkElement);
    }

    private insertHrefLang(langCode: string, url) {
        const linkElement = this.renderer.createElement('link');
        this.renderer.setAttribute(linkElement, 'rel', 'alternate');
        this.renderer.setAttribute(linkElement, 'hreflang', langCode);
        const alternatUrl = this.getUrlHost(langCode, url);
        this.alternatUrls.push(alternatUrl);
        this.renderer.setAttribute(linkElement, 'href', alternatUrl);
        this.insertElement(linkElement);
        this.elementsToBeRemoved.push(linkElement);

    }

    /**
     * x-default-Link fuer den generischen Pfad. Bewusst NICHT in `alternatUrls`
     * gepusht: x-default zeigt auf EN — auf einer Nicht-EN-Seite darf das in
     * `handleNoIndexCase()` keinen Self-Match herstellen.
     */
    private insertXDefault(url: string) {
        const linkElement = this.renderer.createElement('link');
        this.renderer.setAttribute(linkElement, 'rel', 'alternate');
        this.renderer.setAttribute(linkElement, 'hreflang', 'x-default');
        this.renderer.setAttribute(linkElement, 'href', url);
        this.insertElement(linkElement);
        this.elementsToBeRemoved.push(linkElement);
    }

    /**
     * Some pages should not be indexed so we set the corresponding attribute
     */
    private handleNoIndexCase() {
        const currentUrl = normalizeUrlForIndexCheck(window.location.href);
        const findIndex = this.alternatUrls.findIndex(
            (alternateUrl) => normalizeUrlForIndexCheck(alternateUrl) === currentUrl
        );
        if (-1 !== findIndex) {
            this.logger.log(
                ' Alternate Url found',
                findIndex,
                currentUrl,
                this.alternatUrls
            );
        } else {
            this.logger.log(
                'NOT Alternate Url found',
                findIndex,
                currentUrl,
                this.alternatUrls
            );
            this.logger.log('handleNoIndexCase')
            this.addNoIndex();
        }
    }

    private addNoIndex() {
        //        <meta name="robots" content="noindex">
        this.logger.log('addNoIndex', this.id);
        if (!this.noIndexElement) {
            // make sure we have only one noindex-element
            this.noIndexElement = this.renderer.createElement('meta');
            this.renderer.setAttribute(this.noIndexElement, 'name', 'robots');
            this.renderer.setAttribute(this.noIndexElement, 'content', 'noindex');
            this.insertElement(this.noIndexElement);
        }
    }

    private clearNoIndexElement() {
        if (this.noIndexElement) {
            this.renderer.removeChild(this.document.head, this.noIndexElement);
            this.noIndexElement = null;
        }
    }

    private insertElement(newElement) {
        const refElement = this.document.getElementById(
            'alternate-hreflang-placeholder'
        );
        if (refElement) {
            this.renderer.insertBefore(this.document.head, newElement, refElement);
        } else {
            this.renderer.appendChild(this.document.head, newElement);
        }
    }

    private getUrlHost(langCode: string, url: string): string {
        return environment.host_url + '/' + langCode + '/' + url;
    }

    private removeAlternateHrefLangs() {
        this.elementsToBeRemoved.forEach((linkElement) => {
            this.renderer.removeChild(this.document.head, linkElement);
        });
        this.elementsToBeRemoved = [];
    }

    /** Aktuelle Locale als SamplePollLanguage (unbekannte Locale -> en). */
    private samplePollLanguage(): SamplePollLanguage {
        const short = this.trans.local as SamplePollLanguage;
        return SAMPLE_POLL_LANGUAGES.includes(short) ? short : 'en';
    }

    private updateHtmlTitle(id: string) {
        this.logger.log('updateHtmlTitle');
        this.htmlTitle.setTitle(this.trans.getSeoTitle(id));
    }

    private updateHtmlMetaDescription(id: string) {
        this.htmlMeta.updateTag({
            name: 'description',
            content: this.trans.getSeoDescription(id),
        });
    }

    /**
     * Setzt <title> und <meta description> dynamisch aus den Header-Feldern
     * eines Sample-Polls. Stellt zusaetzlich sicher, dass kein noindex-Tag
     * gesetzt ist — Sample-Polls sind explizit fuer Suchmaschinen indexable.
     *
     * Sprach-Logik: `lang` ist die Sprache des PUBLIZIERTEN Polls
     * (`resolvedLang` aus dem Resolver). Bei EN-Fallback unterscheidet sich
     * `lang` von der URL-Sprache — die SEO-Strings werden trotzdem in `lang`
     * gewaehlt, damit Title und Description zur Canonical-URL passen, die
     * `setupSamplePollAlternates` parallel auf die EN-Variante setzt.
     *
     * Title-/Description-Logik (Budgets, Suffix-Kaskade, Fallback-Chain) lebt
     * in `buildPollMeta` (whocan-lib) — geteilt mit der ogProxy Cloud Function,
     * damit Client und Function garantiert identische Tags erzeugen.
     *
     * Wird von der SamplePollVotePage aufgerufen sobald der Header geladen ist.
     */
    public setSamplePollMeta(header: HeaderClass, lang: SamplePollLanguage) {
        this.logger.log('setSamplePollMeta', header?.pollTitle, lang);
        const meta = buildPollMeta(header, lang, 'samplePoll', environment.brandName);
        this.htmlTitle.setTitle(meta.title);
        this.htmlMeta.updateTag({ name: 'description', content: meta.description });

        // Sample-Polls sind explizit indexable
        this.clearNoIndexElement();
    }

    /**
     * VERFEINERT Canonical + hreflang eines Sample-Polls aus den Resolver-Keys — best effort.
     * Der erste, garantiert sichtbare Stand kommt URL-basiert und ohne Fetch aus
     * `setSamplePollAlternatesFromUrl` (auf NavigationEnd). Diese Methode laeuft erst nach dem
     * RTDB-Read und ist fuer Googlebot nie erreichbar (robots.txt-Block, s.o.); fuer echte
     * User/Dev praezisiert sie:
     * - hreflang NUR fuer publizierte Sprachen — nicht publizierte Locale-URLs sind zwar
     *   per EN-Fallback erreichbar, kanonisieren aber auf EN weg und duerfen darum nicht
     *   im Cluster auftauchen (Canonical und hreflang muessen dieselbe Geschichte
     *   erzaehlen; Audit sample-poll-absorption-2026-08-25 §2, gleiche Regel im ogProxy)
     * - Canonical zeigt bei Fallback auf die EN-URL, sonst auf die aufgeloeste Locale-URL
     * - x-default: bevorzugt EN-URL, sonst Canonical
     *
     * Wichtig: ersetzt den frueh gesetzten URL-Canonical nur, loescht ihn nie — getrenntes
     * Element-Tracking (Canonical vs. hreflang) stellt das sicher.
     */
    public async setupSamplePollAlternates(keys: SamplePollResolvedKeys): Promise<void> {
        this.logger.log('setupSamplePollAlternates', keys);
        // Late-Write-Guard: die Page ruft diese Methode erst nach ihrem RTDB-Read auf.
        // Ist der User bis dahin weiternavigiert (SPA), duerfen die Sample-Poll-Tags
        // nicht mehr auf die neue Route geschrieben werden.
        if (keys.slug !== this.activeSamplePollSlug) {
            return;
        }
        // Canonical aus den Keys verfeinern (idempotent: No-op, wenn der URL-Self-Canonical
        // aus setSamplePollAlternatesFromUrl schon stimmt; biegt bei isFallback auf EN um).
        const canonicalUrl = this.setSamplePollCanonical(keys);

        const variants = await this.samplePollSlugService.getAllLanguageVariants(keys.slug);
        // Guard erneut: waehrend getAllLanguageVariants kann navigiert worden sein.
        if (keys.slug !== this.activeSamplePollSlug) {
            return;
        }
        const hreflangVariants = variants.map(v => ({
            lang: v.lang,
            url: this.buildSamplePollUrl(v.lang, keys.slug),
        }));

        this.writeSamplePollHreflang(hreflangVariants, canonicalUrl);
    }

    /**
     * Setzt Canonical + hreflang + x-default eines Sample-Polls REIN AUS DER URL —
     * null Firebase-Reads.
     *
     * Hintergrund: Firebases robots.txt blockt Googlebot fuer die RTDB-Long-Polling-Requests
     * (`*.firebaseio.com/.lp`). Der `samplePollResolver` macht aber pro Aufloesung einen
     * RTDB-Read (ownerId/pollId/resolvedLang/isFallback); fuer Googlebot wird der geblockt, die
     * Route aktiviert nie mit echten Daten, und ein resolver-basierter Canonical bliebe
     * unsichtbar. Slug + URL-Sprache stehen aber schon im NavigationEnd-Snapshot fest, also
     * brauchen Canonical/hreflang gar keinen Fetch.
     *
     * Self-Canonical auf die saubere lokalisierte URL, hreflang fuer alle 6 Sprachen,
     * x-default -> EN. Die Slugs der Detail-Routen sind sprachuebergreifend identisch, nur der
     * Listing-Pfad ist lokalisiert (`SAMPLE_POLLS_LIST_PATH_BY_LANG`), damit deckt das alle
     * Sprachen ab. `setupSamplePollAlternates` darf das spaeter best-effort verfeinern (echte
     * User/Dev): hreflang auf existierende Varianten eingrenzen, bei Fallback den Canonical auf
     * EN umbiegen — aber nur ERSETZEN, nicht loeschen.
     */
    public setSamplePollAlternatesFromUrl(slug: string, urlLang: SamplePollLanguage): void {
        this.activeSamplePollSlug = slug;
        this.removeServerRenderedSeoLinks();
        const canonicalUrl = this.buildSamplePollUrl(urlLang, slug);
        this.writeSamplePollCanonicalUrl(canonicalUrl);
        const hreflangVariants = SAMPLE_POLL_LANGUAGES.map(lang => ({
            lang,
            url: this.buildSamplePollUrl(lang, slug),
        }));
        const enUrl = this.buildSamplePollUrl('en', slug);
        this.writeSamplePollHreflang(hreflangVariants, enUrl);
    }

    /**
     * Verfeinert den Canonical aus den Resolver-Keys (nur fuer echte User/Dev erreichbar, fuer
     * Googlebot nie — s. setSamplePollAlternatesFromUrl). Bei EN-Fallback zeigt der Canonical
     * auf die EN-URL, sonst auf die aufgeloeste Locale-URL. Idempotent.
     */
    public setSamplePollCanonical(keys: SamplePollResolvedKeys): string {
        const canonicalUrl = keys.isFallback
            ? this.buildSamplePollUrl('en', keys.slug)
            : this.buildSamplePollUrl(keys.resolvedLang, keys.slug);
        this.writeSamplePollCanonicalUrl(canonicalUrl);
        return canonicalUrl;
    }

    /**
     * Schreibt den Canonical-Link idempotent — steht der korrekte schon, passiert nichts
     * (kein Flackern fuer einen Snapshot, der mitten in ein Update faellt). Element wird
     * getrennt von den hreflang-Elementen getrackt, damit ein spaeteres async hreflang-Update
     * den frueh gesetzten URL-Canonical nicht loescht.
     */
    private writeSamplePollCanonicalUrl(canonicalUrl: string): void {
        if (this.samplePollCanonicalElement
            && this.samplePollCanonicalElement.getAttribute('href') === canonicalUrl) {
            return;
        }
        this.removeSamplePollCanonical();
        const canonicalLink = this.renderer.createElement('link');
        this.renderer.setAttribute(canonicalLink, 'rel', 'canonical');
        this.renderer.setAttribute(canonicalLink, 'href', canonicalUrl);
        this.insertElement(canonicalLink);
        this.samplePollCanonicalElement = canonicalLink;
    }

    private buildSamplePollUrl(lang: SamplePollLanguage, slug: string): string {
        return environment.host_url + '/' + lang + SAMPLE_POLLS_LIST_PATH_BY_LANG[lang] + '/' + slug;
    }

    private writeSamplePollHreflang(hreflangVariants: { lang: string; url: string }[], canonicalUrl: string) {
        this.removeSamplePollHreflang();

        // hreflang pro Sprache
        hreflangVariants.forEach(variant => {
            const link = this.renderer.createElement('link');
            this.renderer.setAttribute(link, 'rel', 'alternate');
            this.renderer.setAttribute(link, 'hreflang', variant.lang);
            this.renderer.setAttribute(link, 'href', variant.url);
            this.insertElement(link);
            this.samplePollHreflangElements.push(link);
        });

        // x-default: bevorzugt EN, sonst der Canonical
        const enVariant = hreflangVariants.find(v => v.lang === 'en');
        const defaultUrl = enVariant ? enVariant.url : canonicalUrl;
        const defaultLink = this.renderer.createElement('link');
        this.renderer.setAttribute(defaultLink, 'rel', 'alternate');
        this.renderer.setAttribute(defaultLink, 'hreflang', 'x-default');
        this.renderer.setAttribute(defaultLink, 'href', defaultUrl);
        this.insertElement(defaultLink);
        this.samplePollHreflangElements.push(defaultLink);
    }

    private removeSamplePollAlternates() {
        this.removeSamplePollCanonical();
        this.removeSamplePollHreflang();
    }

    private removeSamplePollCanonical() {
        if (this.samplePollCanonicalElement) {
            this.renderer.removeChild(this.document.head, this.samplePollCanonicalElement);
            this.samplePollCanonicalElement = null;
        }
    }

    private removeSamplePollHreflang() {
        this.samplePollHreflangElements.forEach(el => this.renderer.removeChild(this.document.head, el));
        this.samplePollHreflangElements = [];
    }
}
